import { Alert, Box, Button, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { Bound } from "lib/@components/binding/Bound"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { invalidate } from "lib/graphql/cache"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { confirm } from "lib/@dialogs/confirm"
import { useRoles } from "lib/permissions/roles"
import { getScimGroups, getScimGroupMappings } from "routes/settings/plugins/scim/controller/get-scim-group-mappings"
import {
    deleteScimGroupMapping,
    upsertScimGroupMapping,
} from "routes/settings/plugins/scim/controller/upsert-scim-group-mapping"
import { MappingRow } from "./mapping-row"
import { NewMappingRow } from "./new-mapping-row"
import { SyncedGroupsSection } from "./synced-groups-section"
import { useState, useEffect } from "react"

export function GroupMappingsSection() {
    const { client } = useBoundContext()
    const clientId = client?.id
    const groups = getScimGroups.useResults({ clientId }) ?? []
    const mappings = getScimGroupMappings.useResults({ clientId }) ?? []
    const roles = useRoles({ client: clientId })

    const enrichedMappings = mappings.map(enrichMapping)
    const staleCount = enrichedMappings.filter(isStale).length

    const [forms, setForms] = useState(() => buildForms(mappings))

    // Sync: add entries for new mappings, remove entries for deleted ones.
    // Never overwrite an in-progress edit (we only touch keys not already present).
    useEffect(syncForms, [mappings])

    const dirtyIds = enrichedMappings.map(toId).filter(isMappingDirty)
    const hasDirty = dirtyIds.length > 0

    return (
        <Box sx={{ mt: 4 }}>
            <Typography variant="subtitle1" gutterBottom>
                Group → license mapping
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                When a user joins a mapped IdP group, they're given the license + roles below. Highest priority wins
                when a user is in multiple mapped groups. Manually-set licenses are never overwritten by SCIM, and the
                tenant's primary user is always skipped.
            </Typography>

            <If value={groups.length > 0}>
                <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                    {groups.length} group{groups.length === 1 ? "" : "s"} synced from your IdP.
                </Alert>
            </If>

            <If value={staleCount > 0}>
                <Alert
                    severity="warning"
                    variant="outlined"
                    sx={{ mb: 2 }}
                    action={
                        <Button size="small" color="warning" onClick={cleanStale}>
                            Remove {staleCount} stale
                        </Button>
                    }
                >
                    {staleCount} mapping{staleCount === 1 ? "" : "s"} reference{staleCount === 1 ? "s" : ""} a group no
                    longer present in your IdP.
                </Alert>
            </If>

            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 260 }}>Group</TableCell>
                            <TableCell sx={{ width: 170 }}>License</TableCell>
                            <TableCell>Roles</TableCell>
                            <TableCell sx={{ width: 90 }} align="center">
                                Priority
                            </TableCell>
                            <TableCell sx={{ width: 48 }} align="right" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <Bound forms={forms} roles={roles} onFormChange={handleFormChange}>
                            <Repeat list={enrichedMappings} item={<MappingRow />} keyFn={mappingKeyFn} />
                        </Bound>
                        <NewMappingRow clientId={clientId} knownGroups={groups} rolesAvailable={roles} />
                    </TableBody>
                </Table>
            </Paper>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button variant="contained" disabled={!hasDirty} onClick={saveAll}>
                    Save changes
                </Button>
            </Box>

            <SyncedGroupsSection />
        </Box>
    )

    function mappingKeyFn(m) {
        return m._id
    }

    function syncForms() {
        setForms(mergeMappingsIntoForms)
    }

    function mergeMappingsIntoForms(prev) {
        const next = { ...prev }
        let changed = false
        for (const m of mappings) {
            if (!(m._id in next)) {
                next[m._id] = buildForm(m)
                changed = true
            }
        }
        for (const id of Object.keys(next)) {
            if (!mappings.find(matchId(id))) {
                delete next[id]
                changed = true
            }
        }
        return changed ? next : prev
    }

    function handleFormChange(mappingId, newValues) {
        setForms((prev) => ({ ...prev, [mappingId]: newValues }))
    }

    function isMappingDirty(id) {
        const form = forms[id]
        const original = mappings.find(matchId(id))
        if (!form || !original) return false
        const origLicense = original.license || "none"
        const origRoles = sortedJson(original.roles)
        const formRoles = sortedJson(form.roleIds)
        return form.license !== origLicense || formRoles !== origRoles || form.priority !== (original.priority ?? 0)
    }

    async function saveAll() {
        const toSave = dirtyIds
            .map((id) => ({ id, form: forms[id], mapping: mappings.find(matchId(id)) }))
            .filter(({ mapping }) => mapping)
        try {
            await Promise.all(toSave.map(saveOne))
            invalidate("getScimGroupMappings")
            successSnackbar(
                `${toSave.length} mapping${toSave.length === 1 ? "" : "s"} saved — affected users reconciled`
            )
        } catch (error) {
            errorSnackbar(error?.message || "Save failed")
        }

        function saveOne({ id, form, mapping }) {
            return upsertScimGroupMapping({
                id,
                clientId,
                input: {
                    displayName: mapping.displayName || null,
                    externalId: mapping.externalId || null,
                    license: form.license === "none" ? null : form.license,
                    roles: form.roleIds,
                    priority: form.priority,
                },
            })
        }
    }

    function enrichMapping(m) {
        const matchedByExternal = m.externalId && groups.some(matchByExternal)
        const matchedByName = !m.externalId && m.displayName && groups.some(matchByName)
        return { ...m, _stale: !matchedByExternal && !matchedByName }

        function matchByExternal(g) {
            return g.externalId === m.externalId
        }

        function matchByName(g) {
            return g.displayName === m.displayName
        }
    }

    async function cleanStale() {
        const stale = enrichedMappings.filter(isStale)
        if (
            !(await confirm(
                `Delete ${stale.length} stale mapping${stale.length === 1 ? "" : "s"}? Affected users will be reconciled.`,
                "Remove stale mappings"
            ))
        )
            return
        for (const m of stale) {
            await deleteScimGroupMapping({ id: m._id, clientId })
        }
        invalidate("getScimGroupMappings")
        successSnackbar(`${stale.length} stale mapping${stale.length === 1 ? "" : "s"} removed`)
    }
}

function buildForm(m) {
    return { license: m.license || "none", roleIds: m.roles || [], priority: m.priority ?? 0 }
}

function buildForms(mappings) {
    return Object.fromEntries((mappings || []).map((m) => [m._id, buildForm(m)]))
}

function sortedJson(arr) {
    return JSON.stringify([...(arr || [])].sort())
}

function toId(m) {
    return m._id
}

function matchId(id) {
    return (m) => m._id === id
}

function isStale(m) {
    return m._stale
}
